import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import {
  type VaultProvider,
  getVaultKey,
  hasVaultKey,
  listVaultKeys,
  readVaultSnapshot,
  removeVaultKey,
  setVaultKey,
} from "@/hooks/useKeyVault";
import {
  VAULT_PROVIDERS,
  type VaultConflictResolutions,
  type VaultProvider as SharedVaultProvider,
  type VaultRecoveryPreview,
  type VaultSnapshot,
} from "@shared/profileVault";
import {
  applyRecoveredVaultFromResponse,
  applyVaultMerge,
  deleteCloudVault,
  requestVaultPreview,
} from "@/lib/profileVaultRecovery";
import { Helmet } from "react-helmet-async";
import {
  Activity,
  BrainCircuit,
  Check,
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Lock,
  LogIn,
  Mail,
  Newspaper,
  Shield,
  Trash2,
  Upload,
  Zap,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "wouter";

// ---------------------------------------------------------------------------
// Provider catalogue
// ---------------------------------------------------------------------------

interface ProviderDef {
  id: VaultProvider;
  label: string;
  description: string;
  placeholder: string;
  getUrl: string;
  free: boolean;
  unlocks: string[];
}

const LLM_PROVIDERS: ProviderDef[] = [
  {
    id: "groq",
    label: "Groq",
    description:
      "Primary fast LLM option for Digital Asset Trader and institutional research. Llama-class models on ultra-fast inference. Free tier is generous enough for daily use.",
    placeholder: "gsk_...",
    getUrl: "https://console.groq.com/keys",
    free: true,
    unlocks: [
      "Full TTMA analysis (BTC, ETH, any ticker)",
      "TradFi and Digital Assets institutional research as a fast Gemini alternative",
      "Executive summary + hedge-fund-style PDF/markdown report",
      "8-domain deep dive: technicals, sentiment, derivatives, macro, DeFi, liquidations, order book, price levels",
      "Signal scoring & contrarian alerts",
    ],
  },
  {
    id: "gemini",
    label: "Google Gemini",
    description:
      "Gemini text access depends on the model and account. Google Search grounding used by TSM Analytics is unavailable under the free-only policy. The key stays in your browser vault.",
    placeholder: "AIza...",
    getUrl: "https://aistudio.google.com/app/apikey",
    free: true,
    unlocks: [
      "Gemini remains selectable for compatibility, with an entitlement message before any request",
      "Search-grounded Analytics requires a free-tier capability that is unavailable here",
      "Browser-vault BYOK flow instead of server-side env keys",
    ],
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    description:
      "Free-eligible models through one key. TSM's free-only routes use registry-approved models, including OpenRouter's free model router.",
    placeholder: "sk-or-v1-...",
    getUrl: "https://openrouter.ai/keys",
    free: true,
    unlocks: [
      "Digital Asset Trader, TradFi, and Digital Assets research through OpenRouter",
      "Default model request: `openrouter/free`; the returned model can differ",
    ],
  },
];

const DATA_PROVIDERS: ProviderDef[] = [
  {
    id: "finnhub",
    label: "Finnhub",
    description:
      "Real-time stock quotes, earnings calendars, company fundamentals, and financial news. Free tier: 60 calls/min.",
    placeholder: "d1gc0p...",
    getUrl: "https://finnhub.io/dashboard#api-key",
    free: true,
    unlocks: [
      "Real-time equities quotes",
      "Earnings surprises & estimates",
      "Analyst ratings and price targets",
      "Company financials and insider transactions",
    ],
  },
  {
    id: "alphaVantage",
    label: "Alpha Vantage",
    description:
      "Equities, forex, crypto, economic indicators, and technical indicators. TSM permits up to 25 calls per rolling 24 hours across active consumers.",
    placeholder: "2VBGCF...",
    getUrl: "https://www.alphavantage.co/support/#api-key",
    free: true,
    unlocks: [
      "Equity OHLCV (intraday + daily)",
      "Technical indicators: RSI, MACD, SMA, Bollinger",
      "Economic indicators from US Treasury / Fed",
      "Forex and crypto pair pricing",
    ],
  },
  {
    id: "polygon",
    label: "Polygon.io",
    description:
      "High-quality US equities data: trades, quotes, aggregates, options. Free tier covers delayed data.",
    placeholder: "AV1NO5...",
    getUrl: "https://polygon.io/dashboard",
    free: true,
    unlocks: [
      "Aggregated OHLCV bars",
      "Options chain data",
      "Market holidays & status",
      "News articles linked to tickers",
    ],
  },
  {
    id: "nyt",
    label: "New York Times",
    description:
      "Top Stories API enrichment for global, business, and regional news that may provide financial or economic context. Only the API Key is needed here; the NYT API Secret is not required.",
    placeholder: "nyt_...",
    getUrl: "https://developer.nytimes.com/get-started",
    free: true,
    unlocks: [
      "Top stories enrichment",
      "Business and world-news context",
      "Regional news signals for macro analysis",
    ],
  },
  {
    id: "newsapi",
    label: "NewsAPI",
    description:
      "NewsAPI's Developer plan is for development and cannot be used for production under TSM's free-only policy. Local RSS remains available.",
    placeholder: "7a2b3c...",
    getUrl: "https://newsapi.org/account",
    free: true,
    unlocks: [
      "Expanded news sources beyond built-in RSS feeds",
      "Keyword-filtered market news",
      "Top headlines per country / category",
    ],
  },
  {
    id: "cmc",
    label: "CoinMarketCap",
    description:
      "Authoritative crypto market data: prices, market caps, volume, dominance, and global metrics. Free Basic tier available.",
    placeholder: "b54f2a...",
    getUrl: "https://pro.coinmarketcap.com/account",
    free: true,
    unlocks: [
      "Real-time crypto prices and market caps",
      "Global market dominance and volume data",
      "Exchange listings and liquidity data",
      "TTMA exchange manager enrichment",
    ],
  },
  {
    id: "fmp",
    label: "Financial Modeling Prep",
    description:
      "Equity quotes, market movers, fundamentals, calendars, ratios, and index proxies. Used by Monitor for the Markets card.",
    placeholder: "Fj2bAR...",
    getUrl: "https://site.financialmodelingprep.com/developer/docs",
    free: true,
    unlocks: [
      "SPY / QQQ / DIA / VIX market pulse",
      "Market movers and equity snapshots",
      "Fundamentals and calendar enrichment",
    ],
  },
  {
    id: "fred",
    label: "FRED",
    description:
      "Federal Reserve economic data for yields, spreads, policy rates, inflation, labor, and macro stress inputs.",
    placeholder: "0ddf577...",
    getUrl: "https://fred.stlouisfed.org/docs/api/api_key.html",
    free: true,
    unlocks: [
      "2Y / 10Y Treasury rates",
      "Fed Funds and credit spread context",
      "Macro stress and central-bank watch cards",
    ],
  },
  {
    id: "eia",
    label: "EIA",
    description:
      "US Energy Information Administration data for oil, gas, inventories, production, and energy-market pressure.",
    placeholder: "EM59Y...",
    getUrl: "https://www.eia.gov/opendata/register.php",
    free: true,
    unlocks: [
      "WTI, Brent, and Henry Hub snapshots",
      "Energy complex monitor cards",
      "Oil-to-inflation and airline-risk context",
    ],
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const TierBadge: React.FC<{ label: string; active?: boolean }> = ({ label, active }) => (
  <span className={`gs-tier-badge ${active ? "gs-tier-badge--active" : ""}`}>{label}</span>
);

const UnlockList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="gs-unlock-list" aria-label="Features unlocked">
    {items.map((item) => (
      <li key={item}>
        <Check size={13} aria-hidden="true" />
        {item}
      </li>
    ))}
  </ul>
);

const CollapsibleKeySection: React.FC<{
  id: string;
  titleId: string;
  title: string;
  icon: React.ReactNode;
  summary: React.ReactNode;
  actionLabel: string;
  collapseLabel: string;
  children: React.ReactNode;
}> = ({ id, titleId, title, icon, summary, actionLabel, collapseLabel, children }) => {
  const [open, setOpen] = useState(false);
  const panelId = `${id}-panel`;

  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === `#${id}`) setOpen(true);
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [id]);

  return (
    <section className={`gs-key-section ${open ? "gs-key-section--open" : ""}`} id={id} aria-labelledby={titleId}>
      <div className="gs-section-header">
        {icon}
        <h2 id={titleId}>{title}</h2>
      </div>
      <p className="gs-section-sub">{summary}</p>
      <button
        type="button"
        className="gs-section-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{open ? collapseLabel : actionLabel}</span>
        <ChevronRight size={14} aria-hidden="true" />
      </button>
      <div id={panelId} hidden={!open}>
        {children}
      </div>
    </section>
  );
};

const ProviderRow: React.FC<{ def: ProviderDef }> = ({ def }) => {
  const [value, setValue] = useState(() => getVaultKey(def.id) ?? "");
  const [visible, setVisible] = useState(false);
  const [saved, setSaved] = useState(() => hasVaultKey(def.id));

  const handleSave = () => {
    setVaultKey(def.id, value);
    setSaved(hasVaultKey(def.id));
  };

  const handleRemove = () => {
    removeVaultKey(def.id);
    setValue("");
    setSaved(false);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
  };

  return (
    <div className={`gs-provider-row ${saved ? "gs-provider-row--saved" : ""}`}>
      <div className="gs-provider-meta">
        <div className="gs-provider-header">
          <span className="gs-provider-name">{def.label}</span>
          {def.free && <TierBadge label="FREE TIER" />}
          {saved && <TierBadge label="KEY SAVED" active />}
        </div>
        <p className="gs-provider-desc">{def.description}</p>
        <UnlockList items={def.unlocks} />
        <a
          className="gs-get-key-link"
          href={def.getUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Get {def.label} key <ChevronRight size={13} aria-hidden="true" />
        </a>
      </div>

      <div className="gs-key-input-group">
        <div className="gs-key-input-row">
          <input
            type={visible ? "text" : "password"}
            className="gs-key-input"
            placeholder={def.placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKey}
            aria-label={`${def.label} API key`}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            className="gs-key-vis-btn"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Hide key" : "Show key"}
          >
            {visible ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
          <button
            type="button"
            className="gs-key-save-btn"
            onClick={handleSave}
            disabled={!value.trim()}
            aria-label={`Save ${def.label} key`}
          >
            {saved ? <Check size={15} /> : <KeyRound size={15} />}
            <span>{saved ? "Saved" : "Save key"}</span>
          </button>
          {saved && (
            <button
              type="button"
              className="gs-key-remove-btn"
              onClick={handleRemove}
              aria-label={`Remove ${def.label} key`}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
        <p className="gs-key-note">
          <Lock size={11} aria-hidden="true" /> Stored in your browser and sent only when a BYOK request needs that provider.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Vault export / import
// ---------------------------------------------------------------------------

interface AuthStatus {
  googleEnabled: boolean;
  user: null | {
    email: string;
    displayName: string | null;
    pictureUrl: string | null;
  };
  vaultProviders: VaultProvider[];
}

const VAULT_PROVIDER_LABELS: Record<SharedVaultProvider, string> = {
  groq: "Groq",
  openrouter: "OpenRouter",
  gemini: "Google Gemini",
  anthropic: "Anthropic",
  openai: "OpenAI",
  finnhub: "Finnhub",
  alphaVantage: "Alpha Vantage",
  polygon: "Polygon.io",
  nyt: "New York Times",
  newsapi: "NewsAPI",
  cmc: "CoinMarketCap",
  fmp: "Financial Modeling Prep",
  fred: "FRED",
  eia: "EIA",
};

function formatRecoveryTimestamp(value: string | null): string {
  if (!value) return "saved time unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "saved time unavailable" : date.toLocaleString();
}

async function fetchAuthStatus(): Promise<AuthStatus> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  if (!res.ok) throw new Error("Could not read sign-in status");
  return await res.json() as AuthStatus;
}

export const ProfileSync: React.FC = () => {
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [status, setStatus] = useState<"idle" | "working" | "ok" | "err">("idle");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState<VaultRecoveryPreview | null>(null);
  const [reviewedLocalVault, setReviewedLocalVault] = useState<VaultSnapshot | null>(null);
  const [resolutions, setResolutions] = useState<VaultConflictResolutions>({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  const refresh = async () => {
    try {
      setAuth(await fetchAuthStatus());
    } catch {
      setAuth({ googleEnabled: false, user: null, vaultProviders: [] });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const signIn = () => {
    window.location.href = "/api/auth/google?returnTo=/get-started";
  };

  const signOut = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    await refresh();
  };

  const reviewRecovery = async () => {
    setStatus("working");
    try {
      const localVault = readVaultSnapshot();
      setPreview(await requestVaultPreview(localVault));
      setReviewedLocalVault(localVault);
      setResolutions({});
      setMessage("Recovery review ready. No browser keys have changed.");
      setStatus("ok");
    } catch {
      setMessage("Could not review cloud recovery. Try again after signing in.");
      setStatus("err");
    }
  };

  const applyRecovery = async () => {
    if (!preview || !reviewedLocalVault) return;
    setStatus("working");
    try {
      const response = await applyVaultMerge(reviewedLocalVault, preview.revision, resolutions);
      if (response.status === "stale") {
        setPreview(response.preview);
        setResolutions({});
        setMessage("Cloud recovery changed in another browser. Review the updated differences before applying.");
        setStatus("err");
        return;
      }
      applyRecoveredVaultFromResponse(response);
      setPreview(null);
      setReviewedLocalVault(null);
      setMessage(`${response.summary.providers.length} provider key${response.summary.providers.length === 1 ? "" : "s"} safely merged into this browser.`);
      setStatus("ok");
      await refresh();
    } catch {
      setMessage("Could not apply cloud recovery. Review the differences and try again.");
      setStatus("err");
    }
  };

  const removeCloudRecovery = async () => {
    if (deleteConfirmation !== "DELETE CLOUD RECOVERY") return;
    setStatus("working");
    try {
      await deleteCloudVault(deleteConfirmation);
      setPreview(null);
      setReviewedLocalVault(null);
      setResolutions({});
      setDeleteOpen(false);
      setDeleteConfirmation("");
      setMessage("Cloud recovery copy removed. Browser-local keys were left unchanged.");
      setStatus("ok");
      await refresh();
    } catch {
      setMessage("Could not remove the cloud recovery copy. Try again.");
      setStatus("err");
    }
  };

  const savedLocal = listVaultKeys().length;
  const savedProfile = auth?.vaultProviders.length ?? 0;
  const conflicts = preview?.providers.filter((provider) => provider.state === "conflict") ?? [];
  const conflictsResolved = conflicts.every((provider) => resolutions[provider.provider]);
  const canApply = Boolean(preview && preview.providers.length > 0 && conflictsResolved && status !== "working");

  return (
    <section className="gs-vault-portability" aria-label="Cloud recovery">
      <div className="gs-vault-portability-info">
        <strong>{auth?.user ? `Cloud recovery for ${auth.user.email}` : "Cloud recovery"}</strong>
        <p>
          Review a safe, non-destructive merge before recovering keys after browser storage loss or switching devices.
          A missing key in one browser never deletes a key held by the other.
        </p>
      </div>
      <div className="gs-vault-portability-actions">
        {auth?.user ? (
          <>
            <button type="button" className="gs-export-btn" data-recovery-review onClick={reviewRecovery} disabled={status === "working"}>
              <Shield size={15} aria-hidden="true" />
              Review recovery
            </button>
            <button type="button" className="gs-key-remove-btn" onClick={signOut} aria-label="Sign out">
              <LogIn size={15} />
            </button>
          </>
        ) : (
          <button type="button" className="gs-export-btn" onClick={signIn} disabled={auth?.googleEnabled === false}>
            <LogIn size={15} aria-hidden="true" />
            Sign in with Google
          </button>
        )}
        {status === "ok" && <span className="gs-import-ok"><Check size={13} /> {message}</span>}
        {status === "err" && <span className="gs-import-err">{message}</span>}
      </div>
      {auth?.user && (
        <p className="gs-recovery-count" aria-live="polite">
          This browser has {savedLocal} saved provider key{savedLocal === 1 ? "" : "s"}; cloud metadata lists {savedProfile}.
        </p>
      )}
      {auth?.user && preview && (
        <div className="gs-recovery-review" aria-live="polite">
          <strong>Recovery review</strong>
          {preview.providers.length === 0 ? (
            <p>No supported keys were found in this browser or cloud recovery copy.</p>
          ) : (
            <div className="gs-recovery-list">
              {preview.providers.map((provider) => (
                <div className={`gs-recovery-row gs-recovery-row--${provider.state}`} key={provider.provider}>
                  <div>
                    <strong>{VAULT_PROVIDER_LABELS[provider.provider]}</strong>
                    {provider.state === "same" && <span>Already matching</span>}
                    {provider.state === "only_local" && <span>Only in this browser</span>}
                    {provider.state === "only_cloud" && <span>Only in cloud recovery</span>}
                    {provider.state === "conflict" && <span>Different key in each location</span>}
                    <small>
                      Browser: {formatRecoveryTimestamp(provider.localSavedAt)} · Cloud: {formatRecoveryTimestamp(provider.cloudSavedAt)}
                    </small>
                  </div>
                  {provider.state === "conflict" && (
                    <fieldset className="gs-recovery-choice">
                      <legend>Choose which copy to keep for {VAULT_PROVIDER_LABELS[provider.provider]}</legend>
                      <button
                        type="button"
                        className={resolutions[provider.provider] === "local" ? "is-selected" : ""}
                        aria-pressed={resolutions[provider.provider] === "local"}
                        onClick={() => setResolutions((current) => ({ ...current, [provider.provider]: "local" }))}
                      >
                        Keep this browser
                      </button>
                      <button
                        type="button"
                        className={resolutions[provider.provider] === "cloud" ? "is-selected" : ""}
                        aria-pressed={resolutions[provider.provider] === "cloud"}
                        onClick={() => setResolutions((current) => ({ ...current, [provider.provider]: "cloud" }))}
                      >
                        Use cloud recovery
                      </button>
                    </fieldset>
                  )}
                </div>
              ))}
            </div>
          )}
          <button type="button" className="gs-export-btn" data-recovery-apply onClick={applyRecovery} disabled={!canApply}>
            <Check size={15} aria-hidden="true" />
            Apply safe merge
          </button>
        </div>
      )}
      {auth?.user && (
        <div className="gs-recovery-delete">
          {!deleteOpen ? (
            <button type="button" className="gs-key-remove-btn" onClick={() => setDeleteOpen(true)} disabled={status === "working"}>
              <Trash2 size={14} aria-hidden="true" /> Remove cloud recovery copy
            </button>
          ) : (
            <div className="gs-recovery-delete-confirm">
              <label htmlFor="cloud-recovery-confirmation">Type DELETE CLOUD RECOVERY to remove only the encrypted cloud copy.</label>
              <input
                id="cloud-recovery-confirmation"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                autoComplete="off"
              />
              <button type="button" className="gs-key-remove-btn" onClick={removeCloudRecovery} disabled={deleteConfirmation !== "DELETE CLOUD RECOVERY" || status === "working"}>
                Remove cloud copy
              </button>
              <button type="button" className="gs-import-btn" onClick={() => { setDeleteOpen(false); setDeleteConfirmation(""); }} disabled={status === "working"}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
      <p className="gs-key-note" style={{ marginTop: 8 }}>
        <Lock size={11} aria-hidden="true" /> Cloud recovery covers supported API keys only. SMTP sender, password, and recipient settings remain in the offline export/import backup.
      </p>
    </section>
  );
};

function exportVault() {
  const keys: Record<string, string> = {};
  for (const p of VAULT_PROVIDERS) {
    const v = getVaultKey(p);
    if (v) keys[p] = v;
  }
  const email: Record<string, string> = {};
  const sender   = localStorage.getItem("tsm_email_sender");
  const password = localStorage.getItem("tsm_email_password");
  const receiver = localStorage.getItem("tsm_email_receiver");
  if (sender)   email.sender   = sender;
  if (password) email.password = password;
  if (receiver) email.receiver = receiver;

  const payload = {
    version: 1,
    exported_at: new Date().toISOString(),
    note: "TSM credential vault — keep this file private",
    keys,
    email,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `tsm-vault-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function importVault(file: File, onDone: (count: number) => void) {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target?.result as string);
      let count = 0;
      if (data.keys && typeof data.keys === "object") {
        for (const [p, v] of Object.entries(data.keys)) {
          if (typeof v === "string" && v.trim()) {
            setVaultKey(p as VaultProvider, v.trim());
            count++;
          }
        }
      }
      if (data.email && typeof data.email === "object") {
        if (data.email.sender)   { localStorage.setItem("tsm_email_sender",   data.email.sender);   count++; }
        if (data.email.password) { localStorage.setItem("tsm_email_password", data.email.password); count++; }
        if (data.email.receiver) { localStorage.setItem("tsm_email_receiver", data.email.receiver); count++; }
      }
      onDone(count);
    } catch {
      onDone(-1); // signal parse error
    }
  };
  reader.readAsText(file);
}

const VaultPortability: React.FC = () => {
  const [importStatus, setImportStatus] = useState<"idle" | "ok" | "err">("idle");
  const [importCount, setImportCount]   = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    importVault(file, (count) => {
      if (count < 0) {
        setImportStatus("err");
      } else {
        setImportCount(count);
        setImportStatus("ok");
        setTimeout(() => { setImportStatus("idle"); window.location.reload(); }, 2000);
      }
    });
    e.target.value = "";
  };

  return (
    <div className="gs-vault-portability">
      <div className="gs-vault-portability-info">
        <strong>Export &amp; Import credentials</strong>
        <p>
          Download all your saved keys and email credentials as a JSON file. If you ever clear
          your browser storage or switch devices, import that file to restore everything instantly —
          no re-entry needed.
        </p>
      </div>
      <div className="gs-vault-portability-actions">
        <button type="button" className="gs-export-btn" onClick={exportVault}>
          <Download size={15} aria-hidden="true" />
          Export vault
        </button>
        <button type="button" className="gs-import-btn" onClick={() => fileRef.current?.click()}>
          <Upload size={15} aria-hidden="true" />
          Import vault
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          style={{ display: "none" }}
          onChange={handleImport}
          aria-label="Import vault JSON file"
        />
        {importStatus === "ok"  && <span className="gs-import-ok"><Check size={13} /> {importCount} credential{importCount !== 1 ? "s" : ""} imported — reloading…</span>}
        {importStatus === "err" && <span className="gs-import-err">Invalid file — must be a TSM vault export (.json)</span>}
      </div>
      <p className="gs-key-note" style={{ marginTop: 8 }}>
        <Lock size={11} aria-hidden="true" /> The export file contains your keys in plaintext — store it somewhere private (password manager, encrypted drive).
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Email credentials section (3-field, stored in localStorage directly)
// ---------------------------------------------------------------------------

const EMAIL_KEYS = {
  sender:   "tsm_email_sender",
  password: "tsm_email_password",
  receiver: "tsm_email_receiver",
} as const;

function readEmail(k: keyof typeof EMAIL_KEYS): string {
  return localStorage.getItem(EMAIL_KEYS[k]) ?? "";
}
function saveEmail(k: keyof typeof EMAIL_KEYS, v: string): void {
  if (v.trim()) localStorage.setItem(EMAIL_KEYS[k], v.trim());
  else localStorage.removeItem(EMAIL_KEYS[k]);
}

const EmailSection: React.FC = () => {
  const [sender,   setSender]   = useState(() => readEmail("sender"));
  const [password, setPassword] = useState(() => readEmail("password"));
  const [receiver, setReceiver] = useState(() => readEmail("receiver"));
  const [showPw, setShowPw]     = useState(false);
  const [saved, setSaved]       = useState(
    () => !!(readEmail("sender") && readEmail("password") && readEmail("receiver"))
  );

  const handleSave = () => {
    saveEmail("sender",   sender);
    saveEmail("password", password);
    saveEmail("receiver", receiver);
    setSaved(!!(sender.trim() && password.trim() && receiver.trim()));
  };

  const handleClear = () => {
    (["sender", "password", "receiver"] as const).forEach((k) => saveEmail(k, ""));
    setSender(""); setPassword(""); setReceiver("");
    setSaved(false);
  };

  return (
    <div className={`gs-provider-row ${saved ? "gs-provider-row--saved" : ""}`}>
      <div className="gs-provider-meta">
        <div className="gs-provider-header">
          <span className="gs-provider-name">Gmail SMTP</span>
          {saved && <TierBadge label="SAVED" active />}
        </div>
        <p className="gs-provider-desc">
          Used by the Analytics report email feature. Requires a Gmail account with a{" "}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="gs-get-key-link" style={{ display: "inline" }}>
            16-character App Password
          </a>{" "}
          (not your regular Gmail password). Two-factor authentication must be enabled on the account first.
        </p>
        <UnlockList items={[
          "Send TTMA reports by email from the Analytics page",
          "Works with saved and newly generated Analytics reports",
          "Gmail App Password keeps your real password private",
        ]} />
      </div>

      <div className="gs-key-input-group">
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
          <div>
            <label className="gs-email-label">EMAIL_SENDER — your Gmail address</label>
            <input
              type="email"
              className="gs-key-input"
              placeholder="you@gmail.com"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              autoComplete="off"
              aria-label="Sender email address"
            />
          </div>
          <div>
            <label className="gs-email-label">
              EMAIL_PASSWORD — Gmail App Password{" "}
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="gs-get-key-link" style={{ display: "inline", fontSize: 11 }}>
                Get one <ChevronRight size={10} style={{ display: "inline" }} />
              </a>
            </label>
            <div className="gs-key-input-row">
              <input
                type={showPw ? "text" : "password"}
                className="gs-key-input"
                placeholder="xxxx xxxx xxxx xxxx"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                aria-label="Gmail App Password"
                spellCheck={false}
              />
              <button
                type="button"
                className="gs-key-vis-btn"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="gs-email-label">EMAIL_RECEIVER — where reports are delivered</label>
            <input
              type="email"
              className="gs-key-input"
              placeholder="inbox@example.com"
              value={receiver}
              onChange={(e) => setReceiver(e.target.value)}
              autoComplete="off"
              aria-label="Report recipient email"
            />
          </div>
        </div>

        <div className="gs-key-input-row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="gs-key-save-btn"
            onClick={handleSave}
            disabled={!sender.trim() || !password.trim() || !receiver.trim()}
          >
            {saved ? <Check size={15} /> : <Mail size={15} />}
            <span>{saved ? "Saved" : "Save credentials"}</span>
          </button>
          {saved && (
            <button type="button" className="gs-key-remove-btn" onClick={handleClear} aria-label="Clear email credentials">
              <Trash2 size={15} />
            </button>
          )}
        </div>
        <p className="gs-key-note">
          <Lock size={11} aria-hidden="true" /> Stored in your browser and sent only when you request an emailed report.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const GetStarted: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Get Started — BYOK API Keys & Credential Vault | Tylee Smart Markets</title>
        <meta name="description" content="Add your own API keys to unlock TTMA and TradFi analytics. Groq, Gemini, OpenRouter, Finnhub, Alpha Vantage, Polygon, CoinMarketCap, and more. Keys stored in your browser." />
        <link rel="canonical" href="https://tyl.ee/get-started" />
        <meta property="og:title" content="BYOK Credential Vault — Tylee Smart Markets" />
        <meta property="og:description" content="Your keys, your cost, your models. Add Groq, Gemini, OpenRouter, Finnhub, Alpha Vantage, and 10+ providers. Keys live in your browser." />
        <meta property="og:url" content="https://tyl.ee/get-started" />
        <meta name="twitter:title" content="BYOK Credential Vault — Tylee Smart Markets" />
        <meta name="twitter:description" content="Your keys, your cost, your models. Add Groq, Gemini, OpenRouter, Finnhub, Alpha Vantage, and 10+ providers. Keys live in your browser." />
      </Helmet>
      <div className="tsm-home-shell theme-amber">
        <a href="#gs-main" className="skip-link">Skip to main content</a>

        <AppHeader />

      <main id="gs-main" className="gs-main">

        {/* Hero */}
        <section className="gs-hero" aria-labelledby="gs-title">
          <div>
            <p className="home-kicker">GET STARTED · BYOK · YOUR KEYS, YOUR COST, YOUR MODELS</p>
            <h1 id="gs-title">Your research, your keys.</h1>
            <p className="gs-hero-sub">
              TSM news is free and open — no sign-in required. Analytics and AI reports require
              API keys from the providers you choose. Keys stay in your browser unless you create
              an account and opt in to encrypted cloud sync.
            </p>
          </div>
          <div className="gs-hero-quick">
            <p className="home-kicker">FASTEST PATH</p>
            <ol className="gs-quick-steps">
              <li><span>1</span> Add Groq, Gemini, or OpenRouter below</li>
              <li><span>2</span> Paste the key and click Save key</li>
              <li><span>3</span> Open Analytics and choose that provider</li>
            </ol>
          </div>
        </section>

        {/* Access tier cards */}
        <section className="gs-tiers" aria-labelledby="gs-tiers-title">
          <h2 id="gs-tiers-title" className="gs-section-title">Access tiers</h2>
          <div className="gs-tier-grid">

            <div className="gs-tier-card gs-tier-card--active">
              <div className="gs-tier-icon" aria-hidden="true"><Globe size={22} /></div>
              <strong>Guest — Free forever</strong>
              <p>No key, no account. Live news plus demo AI runs so visitors can try the research tools first.</p>
              <ul className="gs-tier-list">
                <li><Check size={12} aria-hidden="true" /> Live market &amp; world news (RSS)</li>
                <li><Check size={12} aria-hidden="true" /> Bottom tape — scrollable, up to 1,000 headlines</li>
                <li><Check size={12} aria-hidden="true" /> Full news terminal (/news)</li>
                <li><Check size={12} aria-hidden="true" /> 4 demo AI tool runs per hour at limited rates</li>
                <li><Check size={12} aria-hidden="true" /> Guest compact prompts for experimenting before BYOK</li>
              </ul>
              <Link href="/news" className="gs-tier-cta">Open news terminal</Link>
            </div>

            <div className="gs-tier-card gs-tier-card--recommended">
              <div className="gs-tier-icon" aria-hidden="true"><KeyRound size={22} /></div>
              <div className="gs-tier-rec-badge">RECOMMENDED</div>
              <strong>Browser Vault — BYOK, no account</strong>
              <p>
                Add your own keys below. Keys live in your browser's local storage — zero account
                needed. Unlocks the full TTMA analytics engine immediately.
              </p>
              <ul className="gs-tier-list">
                <li><Check size={12} aria-hidden="true" /> Everything in Guest</li>
                <li><Check size={12} aria-hidden="true" /> TTMA analytics — any ticker with Groq or OpenRouter free-eligible models</li>
                <li><Check size={12} aria-hidden="true" /> Hedge-fund style reports: markdown, JSON, email, print</li>
                <li><Check size={12} aria-hidden="true" /> 8-domain deep analysis: technicals, macro, DeFi &amp; more</li>
                <li><Check size={12} aria-hidden="true" /> TradFi research — Groq or OpenRouter; Gemini Search is unavailable under the free-only policy</li>
                <li><Check size={12} aria-hidden="true" /> Model picker — Gemini, OpenRouter, Claude, and Qwen via provider keys</li>
                <li><Check size={12} aria-hidden="true" /> Enhanced market data (Finnhub, Alpha Vantage, Polygon)</li>
              </ul>
              <a href="#gs-llm" className="gs-tier-cta gs-tier-cta--primary">
                <Zap size={14} aria-hidden="true" /> Add keys now
              </a>
            </div>

            <div className="gs-tier-card gs-tier-card--soon">
              <div className="gs-tier-icon" aria-hidden="true"><Shield size={22} /></div>
              <div className="gs-tier-soon-badge">LIVE</div>
              <strong>Account + Cloud Sync</strong>
              <p>
                Sign in with Google. Keys encrypted server-side, synced across devices. Never
                re-enter keys on a new machine.
              </p>
              <ul className="gs-tier-list">
                <li><Check size={12} aria-hidden="true" /> Everything in Browser Vault</li>
                <li><Check size={12} aria-hidden="true" /> Keys encrypted at rest (AES-256-GCM)</li>
                <li><Check size={12} aria-hidden="true" /> Cross-device sync</li>
                <li><Check size={12} aria-hidden="true" /> Saved reports &amp; analysis history</li>
                <li><Check size={12} aria-hidden="true" /> Team sharing (future)</li>
              </ul>
              <button className="gs-tier-cta" type="button" onClick={() => { window.location.href = "/api/auth/google?returnTo=/get-started"; }}>
                <LogIn size={14} aria-hidden="true" /> Sign in with Google
              </button>
            </div>

          </div>
        </section>

        {/* TTMA feature callout */}
        <section className="gs-feature-callout" aria-label="TTMA capabilities">
          <div className="gs-feature-callout-icon" aria-hidden="true">
            <BrainCircuit size={32} />
          </div>
          <div>
            <p className="home-kicker">TTMA ANALYTICS ENGINE</p>
            <h2>Built like a hedge-fund morning brief.</h2>
            <p>
              One Groq or OpenRouter key unlocks the free-eligible TTMA models — the same analysis system that was
              battle-tested across Binance, Coinbase, Kraken, KuCoin, BingX, Bitget, and MEXC.
              Run it on any ticker and get:
            </p>
            <div className="gs-feature-chips">
              {[
                "Technical analysis (RSI, MACD, Bollinger, SMAs)",
                "Sentiment: Fear & Greed, Stock-to-Flow, MVRV, NVT, Pi Cycle",
                "Derivatives: funding rates, open interest, long/short ratio",
                "DeFi metrics & liquidation detection",
                "Order book analysis",
                "Bullish / bearish scenarios with confidence %",
                "Signal scoring + contrarian alerts",
                "Markdown report — download, email, or print",
              ].map((f) => (
                <span key={f} className="gs-chip">
                  <Activity size={11} aria-hidden="true" /> {f}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Google profile sync */}
        <ProfileSync />

        {/* Export / Import */}
        <VaultPortability />

        {/* LLM keys */}
        <CollapsibleKeySection
          id="gs-llm"
          titleId="gs-llm-title"
          title="AI / LLM keys"
          icon={<BrainCircuit size={18} aria-hidden="true" />}
          actionLabel="Manage AI / LLM keys"
          collapseLabel="Hide AI / LLM keys"
          summary={(
            <>
              Groq and OpenRouter are available on free-eligible Analytics paths. Gemini remains selectable, but TSM blocks Search-grounded requests before provider use under its free-only policy.
              OpenRouter defaults to <code>openrouter/free</code>; the resolved model is shown with each result.
            </>
          )}
        >
          <div className="gs-providers-list">
            {LLM_PROVIDERS.map((def) => (
              <ProviderRow key={def.id} def={def} />
            ))}
          </div>
        </CollapsibleKeySection>

        {/* Market data keys */}
        <CollapsibleKeySection
          id="gs-data"
          titleId="gs-data-title"
          title="Market data keys"
          icon={<Newspaper size={18} aria-hidden="true" />}
          actionLabel="Manage market data keys"
          collapseLabel="Hide market data keys"
          summary={(
            <>
              All have free tiers. Add them to unlock richer TTMA inputs — equities quotes,
              earnings, fundamentals, technical indicators, and expanded news.
            </>
          )}
        >
          <div className="gs-providers-list">
            {DATA_PROVIDERS.map((def) => (
              <ProviderRow key={def.id} def={def} />
            ))}
          </div>
        </CollapsibleKeySection>

        {/* Email credentials */}
        <CollapsibleKeySection
          id="gs-email"
          titleId="gs-email-title"
          title="Email notifications"
          icon={<Mail size={18} aria-hidden="true" />}
          actionLabel="Manage email notifications"
          collapseLabel="Hide email notifications"
          summary={(
            <>
              Optional. Enables report email from the Analytics page and sends your TTMA analysis
              directly to an inbox.
            </>
          )}
        >
          <div className="gs-providers-list">
            <EmailSection />
          </div>
        </CollapsibleKeySection>

        {/* Browser vault note */}
        <section className="gs-vault-note" aria-label="Security note">
          <Lock size={16} aria-hidden="true" />
          <div>
            <strong>Browser Vault security note</strong>
            <p>
              Keys saved here are stored in your browser's <code>localStorage</code> in plaintext.
              They are sent only when a BYOK request needs that provider, or when you choose profile sync.
              If you share this device or use a public computer, clear your vault before leaving. The Account
              tier encrypts keys server-side with AES-256-GCM so you can safely sync across devices.
            </p>
          </div>
        </section>

      </main>
      <Footer />
      </div>
    </>
  );
};

export default GetStarted;
